import { createStore } from '../lib/store';
import { absRequest, sessionIdentity } from './client';
import { FINISHED_THRESHOLD, parseEpochMs } from './helpers';
import { getSession, sessionStore } from './session';
import type { AbsUser, MediaProgress } from './types';

/**
 * The signed-in user's progress for every book, keyed by library item id.
 *
 * ABS list endpoints (library items, shelves, series, search) do not include
 * per-user progress; only `/api/me` and `/api/authorize` do, as
 * `user.mediaProgress`. Like the official web client, Plume joins this map
 * into tiles on the client.
 *
 * Server snapshots are merged, not swapped in: several unsynchronised callers
 * fetch `/api/me`, and a response that started before an optimistic update
 * or playback progress (or one the service worker answered from its cache
 * offline) must not roll those back. Per item the newer `lastUpdate` wins.
 */
export const progressStore = createStore<Record<string, MediaProgress>>({});

/** Items changed on this device and not yet confirmed by a newer server entry. */
const localItems = new Set<string>();
let owner = sessionIdentity(getSession());

function reset(): void {
  localItems.clear();
  progressStore.set({});
}

// Never show one account's progress to the next.
sessionStore.subscribe(() => {
  const next = sessionIdentity(getSession());
  if (next === owner) return;
  owner = next;
  reset();
});

const updatedAt = (p: MediaProgress | undefined): number => parseEpochMs(p?.lastUpdate) ?? 0;

/**
 * Merges the user's `mediaProgress` into the store. Snapshots of another
 * user (a late response after an account switch) are ignored.
 */
export function setProgressFromUser(user: Pick<AbsUser, 'mediaProgress'> & { id?: string } | undefined): void {
  if (!user?.mediaProgress) return;
  const session = getSession();
  if (session && user.id && user.id !== session.userId) return;
  const prev = progressStore.get();
  const next: Record<string, MediaProgress> = {};
  for (const p of user.mediaProgress) {
    if (!p || p.episodeId || !p.libraryItemId) continue;
    const local = prev[p.libraryItemId];
    if (local && localItems.has(p.libraryItemId) && updatedAt(local) > updatedAt(p)) {
      next[p.libraryItemId] = local;
    } else {
      next[p.libraryItemId] = p;
      localItems.delete(p.libraryItemId);
    }
  }
  // Entries the server no longer lists were deleted there, unless this device changed them since.
  for (const id of localItems) {
    const local = prev[id];
    if (local && !next[id]) next[id] = local;
  }
  progressStore.set(next);
}

/**
 * Local update merged into the existing entry (playback progress). For the
 * quick actions use `setProgressMutation`, which writes complete entries.
 */
export function patchProgress(itemId: string, patch: Partial<MediaProgress>): void {
  localItems.add(itemId);
  progressStore.set((all) => {
    const prev = all[itemId];
    return {
      ...all,
      [itemId]: {
        libraryItemId: itemId,
        duration: 0,
        progress: 0,
        currentTime: 0,
        isFinished: false,
        ...prev,
        ...patch,
        lastUpdate: Date.now(),
      },
    };
  });
}

export type ProgressMutation = 'progressReset' | 'markedFinished' | 'markedUnfinished';

export interface ProgressMutationContext {
  /** Seconds; used when the store has no entry (or no duration) for the item. */
  duration?: number;
  /** Seconds listened; used when the store has no entry for the item. */
  currentTime?: number;
  now?: number;
}

/**
 * The complete progress entry after a successful reset / mark finished / mark
 * unfinished (pure). Finished flips the flag (100 %) and keeps the listening
 * position, like the server, so a later "Mark as Unfinished" returns to it
 * (Flutter keeps the progress too). Unfinished keeps the position unless it
 * sits in the finished zone (≥ 98 %), where it would still read as finished.
 */
export function progressAfterMutation(itemId: string, prev: MediaProgress | undefined, mutation: ProgressMutation, ctx: ProgressMutationContext = {}): MediaProgress {
  const now = ctx.now ?? Date.now();
  const duration = prev?.duration || ctx.duration || 0;
  const base: MediaProgress = { ...prev, libraryItemId: itemId, duration, progress: 0, currentTime: 0, isFinished: false, finishedAt: null, lastUpdate: now };
  switch (mutation) {
    case 'markedFinished':
      return { ...base, isFinished: true, finishedAt: now, progress: 1, currentTime: prev?.currentTime ?? ctx.currentTime ?? 0 };
    case 'markedUnfinished': {
      const currentTime = prev?.currentTime ?? ctx.currentTime ?? 0;
      const ratio = duration > 0 ? Math.min(1, Math.max(0, currentTime / duration)) : 0;
      return ratio >= FINISHED_THRESHOLD ? base : { ...base, currentTime, progress: ratio };
    }
    case 'progressReset':
      return base;
  }
}

/** Writes the entry for a successful quick action (see `progressAfterMutation`). Returns it. */
export function setProgressMutation(itemId: string, mutation: ProgressMutation, ctx: ProgressMutationContext = {}): MediaProgress {
  const entry = progressAfterMutation(itemId, progressStore.get()[itemId], mutation, ctx);
  localItems.add(itemId);
  progressStore.set((all) => ({ ...all, [itemId]: entry }));
  return entry;
}

/**
 * After a quick action the server answered, adopts its entry for the item:
 * servers differ on what "Mark as Unfinished" does to the position
 * (Audiobookshelf restarts the book, others keep it), and the player resumes
 * from the server's. Skipped when the item changed again on this device since
 * `entry` (the one `setProgressMutation` wrote) or the request fails. The
 * adopted entry keeps `entry`'s `lastUpdate` when that is newer, so optimistic
 * copies made with it (Home shelves) and older snapshots don't win over it.
 */
export async function confirmProgressMutation(itemId: string, entry: MediaProgress): Promise<MediaProgress | null> {
  const owner = sessionIdentity(getSession());
  let server: MediaProgress | null;
  try {
    server = await absRequest<MediaProgress | null>(`/api/me/progress/${encodeURIComponent(itemId)}`);
  } catch {
    return null;
  }
  if (sessionIdentity(getSession()) !== owner || progressStore.get()[itemId] !== entry) return null;
  if (!server || typeof server !== 'object' || (server.libraryItemId && server.libraryItemId !== itemId)) return null;
  const confirmed: MediaProgress = { ...server, libraryItemId: itemId, lastUpdate: Math.max(updatedAt(server), updatedAt(entry)) };
  progressStore.set((all) => ({ ...all, [itemId]: confirmed }));
  return confirmed;
}
